import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  googleLogin: () => api.get('/api/auth/google'),
  getToken: (email) => api.post('/api/auth/token', null, { params: { email } }),
  sendOTP: (mobile) => api.post('/api/auth/otp/send', { mobile }),
  verifyOTP: (mobile, secretKey, otp) => api.post('/api/auth/otp/verify', { mobile, secret_key: secretKey, otp }),
  emailPasswordLogin: (email, password) => api.post('/api/auth/login', { email, password }),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (email, secretKey, otp, newPassword) => api.post('/api/auth/reset-password', { email, secret_key: secretKey, otp, new_password: newPassword }),
};

// Users API
export const usersAPI = {
  getMe: () => api.get('/api/users/me'),
  updateMe: (data) => api.put('/api/users/me', data),
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/users/me/profile-picture', formData);
  },
  getAllUsers: () => api.get('/api/users/'),
  getAllUsersWithAttendance: (excludeEmails = true) => api.get('/api/users/with-attendance', { params: { exclude_emails: excludeEmails } }),
  getUser: (id) => api.get(`/api/users/${id}`),
  createUser: (data) => api.post('/api/users/', data),
  updateUser: (id, data) => api.put(`/api/users/${id}`, data),
  getEmergencyContacts: () => api.get('/api/users/me/emergency-contacts'),
  addEmergencyContact: (data) => api.post('/api/users/me/emergency-contacts', data),
  deleteEmergencyContact: (id) => api.delete(`/api/users/me/emergency-contacts/${id}`),
};

// News Feed API
export const newsAPI = {
  getAll: (skip = 0, limit = 20) => api.get('/api/news/', { params: { skip, limit } }),
  getOne: (id) => api.get(`/api/news/${id}`),
  create: (data) => api.post('/api/news/', data),
  update: (id, data) => api.put(`/api/news/${id}`, data),
  delete: (id) => api.delete(`/api/news/${id}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/news/upload-image', formData);
  },
};

// Attendance API
export const attendanceAPI = {
  punchIn: () => api.post('/api/attendance/punch-in'),
  punchOut: (notes) => api.post('/api/attendance/punch-out', { notes }),
  getMyAttendance: (month, year) => api.get('/api/attendance/my-attendance', { params: { month, year } }),
  getUserAttendance: (userId, month, year) => api.get(`/api/attendance/user/${userId}`, { params: { month, year } }),
  getToday: () => api.get('/api/attendance/today'),
  getAllEmployeesAttendanceByDate: (date) => api.get('/api/attendance/all-employees-by-date', { params: { start_date: date } }),
  uploadBiometric: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/attendance/upload-biometric', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Leave API
export const leaveAPI = {
  create: (data) => api.post('/api/leaves/', data),
  getMyLeaves: () => api.get('/api/leaves/my-leaves'),
  getPendingLeaves: () => api.get('/api/leaves/pending'),
  getAllLeaves: (status) => api.get('/api/leaves/all', { params: { status } }),
  updateStatus: (id, data) => api.put(`/api/leaves/${id}`, data),
  delete: (id) => api.delete(`/api/leaves/${id}`),
  cancel: (id) => api.put(`/api/leaves/${id}/cancel`),
  getTodayLeaves: () => api.get('/api/leaves/today'),
};

// Work From Home API
export const wfhAPI = {
  create: (data) => api.post('/api/wfh/', data),
  getMyWFH: () => api.get('/api/wfh/my-wfh'),
  getPendingWFH: () => api.get('/api/wfh/pending'),
  getAllWFH: (status) => api.get('/api/wfh/all', { params: { status } }),
  updateStatus: (id, data) => api.put(`/api/wfh/${id}`, data),
  delete: (id) => api.delete(`/api/wfh/${id}`),
  getTodayWFH: () => api.get('/api/wfh/today'),
};

// Holiday API
export const holidayAPI = {
  getAll: (year) => api.get('/api/holidays/', { params: { year } }),
  getOne: (id) => api.get(`/api/holidays/${id}`),
  create: (data) => api.post('/api/holidays/', data),
  update: (id, data) => api.put(`/api/holidays/${id}`, data),
  delete: (id) => api.delete(`/api/holidays/${id}`),
};

// Performance Review API
export const performanceAPI = {
  create: (data) => api.post('/api/performance/', data),
  getMyReviews: () => api.get('/api/performance/my-reviews'),
  getToReview: () => api.get('/api/performance/to-review'),
  getAllReviews: () => api.get('/api/performance/all'),
  getOne: (id) => api.get(`/api/performance/${id}`),
  update: (id, data) => api.put(`/api/performance/${id}`, data),
  delete: (id) => api.delete(`/api/performance/${id}`),
};

// Team API
export const teamAPI = {
  getAll: () => api.get('/api/teams/'),
  getOne: (id) => api.get(`/api/teams/${id}`),
  create: (data) => api.post('/api/teams/', data),
  update: (id, data) => api.put(`/api/teams/${id}`, data),
  addMember: (teamId, userId) => api.post(`/api/teams/${teamId}/members`, { user_id: userId }),
  removeMember: (teamId, userId) => api.delete(`/api/teams/${teamId}/members/${userId}`),
  delete: (id) => api.delete(`/api/teams/${id}`),
};

// Reports API
export const reportsAPI = {
  downloadAttendance: (month, year, userId = null) => {
    const params = { month, year };
    if (userId) params.user_id = userId;
    return api.get('/api/reports/attendance/download', {
      params,
      responseType: 'blob',
    });
  },
  downloadLeaves: (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/api/reports/leaves/download', {
      params,
      responseType: 'blob',
    });
  },
  downloadEmployees: () => api.get('/api/reports/employees/download', { responseType: 'blob' }),
};

// File API
export const fileAPI = {
  getAll: (skip = 0, limit = 100) => api.get('/api/files/', { params: { skip, limit } }),
  getOne: (id) => api.get(`/api/files/${id}`),
  upload: (file, description) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    return api.post('/api/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getDownloadUrl: (id) => api.get(`/api/files/${id}/download`),
  delete: (id) => api.delete(`/api/files/${id}`),
};

// BCP Matrix API
export const bcpMatrixAPI = {
  getAll: () => api.get('/api/bcp-matrix/'),
  getOne: (id) => api.get(`/api/bcp-matrix/${id}`),
  create: (data) => api.post('/api/bcp-matrix/', data),
  update: (id, data) => api.put(`/api/bcp-matrix/${id}`, data),
  delete: (id) => api.delete(`/api/bcp-matrix/${id}`),
  getMyBackupDuties: () => api.get('/api/bcp-matrix/my-backup-duties'),
  getActiveBackups: () => api.get('/api/bcp-matrix/active-backups'),
  getUsersOnLeaveWithBackups: () => api.get('/api/bcp-matrix/users-on-leave/today'),
};

export default api;
